'use client';
import { useState, useRef } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import Link from 'next/link';
import { ArrowLeft, Copy, Check, FileUp } from 'lucide-react';

export default function JsonFormatterPage() {
  const [jsonInput, setJsonInput] = useState('');
  const [formattedOutput, setFormattedOutput] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatJson = () => {
    try {
      setError('');
      if (!jsonInput.trim()) {
        setError('Please enter JSON data');
        return;
      }

      const parsed = JSON.parse(jsonInput);
      const formatted = JSON.stringify(parsed, null, 2);
      setFormattedOutput(formatted);
      setIsValid(true);
    } catch (err) {
      setError('Invalid JSON format. Please check your input.');
      setIsValid(false);
      setFormattedOutput('');
    }
  };

  const minifyJson = () => {
    try {
      setError('');
      if (!jsonInput.trim()) {
        setError('Please enter JSON data');
        return;
      }

      const parsed = JSON.parse(jsonInput);
      const minified = JSON.stringify(parsed);
      setFormattedOutput(minified);
      setIsValid(true);
    } catch (err) {
      setError('Invalid JSON format. Please check your input.');
      setIsValid(false);
      setFormattedOutput('');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(formattedOutput);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFileUpload = (file: File) => {
    if (!file.name.endsWith('.json')) {
      setError('Please upload a JSON file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setJsonInput(text);
      setIsValid(null);
    };
    reader.onerror = () => {
      setError('Error reading file');
    };
    reader.readAsText(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="marketing-theme tool-page">

      <Navbar />

      <main className="tool-page-main flex flex-col items-center">
        <div className="w-full max-w-5xl">
          <Link 
            href="/tools" 
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tools
          </Link>

          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              JSON Formatter & Validator Online
            </h1>
            <p className="text-lg text-muted-foreground">
              Format, validate, and beautify your JSON data
            </p>
          </div>

          {/* File Upload Area - Full Width */}
          <div className="mb-6">
            <label className="text-sm font-medium mb-2 block">Upload JSON File</label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={triggerFileInput}
              className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-all ${
                isDragging 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                  : 'border-gray-300 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-600'
              }`}
            >
              <FileUp className={`w-12 h-12 mx-auto mb-4 ${
                isDragging ? 'text-blue-500' : 'text-gray-400'
              }`} />
              <p className="text-lg font-medium mb-2">
                {isDragging ? 'Drop your file here' : 'Click to upload or drag and drop'}
              </p>
              <p className="text-sm text-muted-foreground">
                Supports .json files
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileInputChange}
                className="hidden"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Section */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">JSON Input</label>
                <Textarea
                  placeholder='{"name":"John","age":30,"city":"New York"}'
                  value={jsonInput}
                  onChange={(e) => {
                    setJsonInput(e.target.value);
                    setIsValid(null);
                  }}
                  className="min-h-[200px] font-mono text-sm"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={formatJson} 
                  className="flex-1"
                >
                  Format (Pretty)
                </Button>
                <Button 
                  onClick={minifyJson} 
                  className="flex-1"
                  variant="outline"
                >
                  Minify
                </Button>
              </div>
              {error && (
                <div className="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                  {error}
                </div>
              )}
              {isValid === true && (
                <div className="text-green-500 text-sm bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                  ✓ Valid JSON
                </div>
              )}
            </div>

            {/* Output Section */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Formatted Output</label>
                <Textarea
                  value={formattedOutput}
                  readOnly
                  placeholder="Your formatted JSON will appear here..."
                  className="min-h-[200px] font-mono text-sm bg-muted"
                />
              </div>
              <Button 
                onClick={copyToClipboard} 
                className="w-full"
                variant="outline"
                disabled={!formattedOutput}
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </Button>
            </div>
          </div>

          {/* Example */}
          <div className="mt-8 p-6 bg-card rounded-lg border">
            <h3 className="font-semibold mb-2">Example JSON:</h3>
            <pre className="text-sm bg-muted p-4 rounded overflow-x-auto">
              {`{"name":"John","age":30,"city":"New York","hobbies":["reading","coding"]}`}
            </pre>
          </div>
        </div>
      </main>

      {/* User Problems & FAQs for SEO */}
      <section className="max-w-4xl mx-auto mt-16 mb-12 p-6 bg-card rounded-lg border border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold mb-4">Common JSON Formatter Questions & Problems</h2>
        <div className="space-y-6">
          <div>
            <p className="font-semibold">1. How do I format JSON online?</p>
            <p className="text-muted-foreground">Paste or upload your JSON data above and click "Format (Pretty)" to beautify your JSON instantly.</p>
          </div>
          <div>
            <p className="font-semibold">2. Can I validate JSON with this tool?</p>
            <p className="text-muted-foreground">Yes, the tool checks your JSON for errors and tells you if it is valid or not.</p>
          </div>
          <div>
            <p className="font-semibold">3. How do I minify JSON?</p>
            <p className="text-muted-foreground">Click the "Minify" button to remove all unnecessary spaces and line breaks from your JSON.</p>
          </div>
          <div>
            <p className="font-semibold">4. Is my JSON data secure?</p>
            <p className="text-muted-foreground">All formatting and validation happens in your browser. Your data is never sent to any server.</p>
          </div>
          <div>
            <p className="font-semibold">5. Can I upload a JSON file?</p>
            <p className="text-muted-foreground">Yes, use the upload area to select a .json file from your device and view or format it here.</p>
          </div>
          <div>
            <p className="font-semibold">6. How do I copy formatted JSON?</p>
            <p className="text-muted-foreground">After formatting, click "Copy to Clipboard" to copy the result for use elsewhere.</p>
          </div>
          <div>
            <p className="font-semibold">7. What if my JSON is invalid?</p>
            <p className="text-muted-foreground">The tool will show an error message and highlight issues so you can fix them easily.</p>
          </div>
          <div>
            <p className="font-semibold">8. Can I use this tool for free?</p>
            <p className="text-muted-foreground">Yes, this online JSON formatter and validator is completely free to use.</p>
          </div>
          <div>
            <p className="font-semibold">9. Does this tool support large JSON files?</p>
            <p className="text-muted-foreground">For very large files, browser performance may vary. For huge files, consider using a desktop editor.</p>
          </div>
          <div>
            <p className="font-semibold">10. Can I use this on mobile?</p>
            <p className="text-muted-foreground">Yes, the tool is mobile-friendly and works in any modern browser.</p>
          </div>
        </div>
      </section>
      <div className="text-center text-xs text-muted-foreground pb-4">online</div>
      <Footer />
    </div>
  );
}
